export const UTILITY_GROUPS = [
    { label: "Smokes / Flashes", keys: ["smokeLineups", "flashLineups"] },
    { label: "Mollies / Nades", keys: ["mollyLineups", "nadeLineups"] },
];

export function buildEmptyMapData(template) {
    const entry = { utilities: {}, roles: {}, stratKnowledge: 0 };
    (template?.utilities || []).forEach((utility) => {
        entry.utilities[utility.key] = 0;
    });
    (template?.roles || []).forEach((role) => {
        entry.roles[role.key] = 0;
    });
    return entry;
}

export function buildInitialState(template, saved) {
    const state = {};
    (template?.maps || []).forEach((map) => {
        const existing = saved?.maps?.[map.key];
        state[map.key] = existing
            ? {
                utilities: {
                    ...buildEmptyMapData(template).utilities,
                    ...existing.utilities,
                },
                roles: {
                    ...buildEmptyMapData(template).roles,
                    ...existing.roles,
                },
                stratKnowledge: existing.stratKnowledge || 0,
            }
            : buildEmptyMapData(template);
    });
    return state;
}

export function isMapFilled(mapData) {
    if (!mapData) return false;
    const values = [
        ...Object.values(mapData.utilities || {}),
        ...Object.values(mapData.roles || {}),
        mapData.stratKnowledge || 0,
    ];
    return values.some((value) => value > 0);
}

export function buildUtilitySliderGroups(utilities) {
    const utilityMap = new Map(
        (utilities || []).map((utility) => [utility.key, utility]),
    );
    const groupedKeys = new Set();

    const groupedUtilities = UTILITY_GROUPS
        .map((group) => {
            const keys = group.keys.filter((key) => utilityMap.has(key));
            keys.forEach((key) => groupedKeys.add(key));
            if (!keys.length) return null;
            return { label: group.label, keys };
        })
        .filter(Boolean);

    const remainingUtilities = (utilities || [])
        .filter((utility) => !groupedKeys.has(utility.key))
        .map((utility) => ({ label: utility.display, keys: [utility.key] }));

    return [...groupedUtilities, ...remainingUtilities];
}

export function getUtilitySliderValue(mapData, keys) {
    if (!keys?.length) return 0;
    const total = keys.reduce(
        (sum, key) => sum + (mapData?.utilities?.[key] || 0),
        0,
    );
    return Math.round(total / keys.length);
}

export function getAverageUtilityScore(mapData) {
    const utilities = mapData?.utilities || {};
    const values = Object.values(utilities);
    if (!values.length) return 0;
    return Math.round(
        values.reduce((sum, value) => sum + (Number(value) || 0), 0) / values.length,
    );
}

export function getLoadoutSkillSummary(mapData) {
    const utility = getAverageUtilityScore(mapData);
    const support = Number(mapData?.roles?.support) || 0;
    const strategy = Number(mapData?.stratKnowledge) || 0;
    const entry = Number(mapData?.roles?.entryFragger) || 0;
    const awp = Number(mapData?.roles?.awper) || 0;

    return [
        { subject: "Utility", value: utility, display: `${utility}/10` },
        { subject: "Strategy", value: strategy, display: `${strategy}/10` },
        { subject: "Entry", value: entry, display: `${entry}/10` },
        { subject: "AWP", value: awp, display: `${awp}/10` },
        { subject: "Support", value: support, display: `${support}/10` },
    ];
}

export function buildLoadoutRadarAxes(mapData) {
    return getLoadoutSkillSummary(mapData);
}

export function getMapCompletionPercent(mapData, template) {
    if (!mapData) return 0;
    const utilityGroups = buildUtilitySliderGroups(template?.utilities);
    const roleKeys = (template?.roles || []).map((role) => role.key);
    const values = [
        ...utilityGroups.map((group) => getUtilitySliderValue(mapData, group.keys)),
        Number(mapData?.stratKnowledge) || 0,
        ...roleKeys.map((key) => Number(mapData?.roles?.[key]) || 0),
    ];

    if (!values.length) return 0;
    const filled = values.filter((value) => value > 0).length;
    return Math.round((filled / values.length) * 100);
}
